export interface PageContext {
  title: string
  url: string
  content: string
  businessInfo: {
    name: string
    description: string
    features: string[]
    benefits: string[]
  }
}

/**
 * Extracts relevant content from the current webpage to provide context to the AI
 */
export function extractPageContext(): PageContext {
  // Get basic page info
  const title = document.title || 'Ask Anything'
  const url = window.location.href

  // Extract main content text (excluding navigation, scripts, etc.)
  const contentSelectors = [
    'main',
    '[role="main"]',
    '.hero-content',
    '.features-section',
    '.customization-section',
    '.final-cta-section',
    'h1, h2, h3',
    'p',
    '.feature-description',
    '.customization-description',
  ]

  let content = ''

  contentSelectors.forEach((selector) => {
    const elements = document.querySelectorAll(selector)
    elements.forEach((el) => {
      const text = el.textContent?.trim()
      if (text && text.length > 10 && !content.includes(text)) {
        content += text + '\n'
      }
    })
  })

  // Extract specific business information
  const businessInfo = extractBusinessInfo()

  // Clean up content - remove excessive whitespace and limit length
  content = content
    .replace(/\s+/g, ' ')
    .replace(/\n\s*\n/g, '\n')
    .trim()
    .slice(0, 2000) // Limit content length for API efficiency

  return {
    title,
    url,
    content,
    businessInfo,
  }
}

/**
 * Extracts specific business information from known page elements
 */
function extractBusinessInfo() {
  const businessInfo = {
    name: 'Ask Anything',
    description: 'Add AI search to your site to drive growth, revenue, and engagement.',
    features: [] as string[],
    benefits: [] as string[],
  }

  // Extract hero subtitle if available
  const heroSubtitle = document.querySelector('.hero-subtitle')?.textContent?.trim()
  if (heroSubtitle) {
    businessInfo.description = heroSubtitle
  }

  // Extract features from features section
  const featureElements = document.querySelectorAll('.feature-item')
  featureElements.forEach((feature) => {
    const title = feature.querySelector('.feature-title')?.textContent?.trim()
    const description = feature.querySelector('.feature-description')?.textContent?.trim()

    if (title && description) {
      businessInfo.features.push(`${title}: ${description}`)
    }
  })

  // Extract customization options
  const customizationElements = document.querySelectorAll('.customization-item')
  customizationElements.forEach((item) => {
    const title = item.querySelector('.customization-title')?.textContent?.trim()
    const description = item.querySelector('.customization-description')?.textContent?.trim()

    if (title && description) {
      businessInfo.benefits.push(`${title}: ${description}`)
    }
  })

  // Fallback features if DOM extraction doesn't work
  if (businessInfo.features.length === 0) {
    businessInfo.features = [
      'Privacy: Enterprise-grade security with no data sharing',
      'Boost Engagement: Transform passive visitors into active users',
      'Easy to Install: Integrate with just a few lines of code',
    ]
  }

  // Fallback benefits if DOM extraction doesn't work
  if (businessInfo.benefits.length === 0) {
    businessInfo.benefits = [
      'Match Branding: Apply your own colors, fonts, and brand assets',
      'Customize Goal: Set specific objectives for optimized AI responses',
      'Advanced Analytics: Track engagement and optimize performance',
    ]
  }

  return businessInfo
}

/**
 * Gets a minimal context for testing or when full extraction isn't needed
 */
export function getMinimalContext(): PageContext {
  return {
    title: document.title || 'Ask Anything',
    url: window.location.href,
    content:
      'Ask Anything helps websites add AI-powered search capabilities to boost engagement and drive growth.',
    businessInfo: {
      name: 'Ask Anything',
      description: 'Add AI search to your site to drive growth, revenue, and engagement.',
      features: [
        'Privacy: Enterprise-grade security with no data sharing',
        'Boost Engagement: Transform passive visitors into active users',
        'Easy to Install: Integrate with just a few lines of code',
      ],
      benefits: [
        'Match Branding: Apply your own colors, fonts, and brand assets',
        'Customize Goal: Set specific objectives for optimized AI responses',
      ],
    },
  }
}
